import React from "react";
import type { LucideIcon } from "lucide-react";
import FieldGroup, { FieldGroupProps } from "./Element/FieldGroup";
import Label, { LabelProps } from "./Element/Label";
import Feedback from "./Element/Feedback";

export interface ChipOption {
    value: string | number;
    label: string;
    Icon?: LucideIcon;
}

type SelectedValue = Array<string | number> | string | number | "";

export interface ChipGroupInputProps {
    id?: string;
    name: string;
    options: ChipOption[];
    value?: SelectedValue;
    onChange?: (value: Array<string | number>) => void;
    disabled?: boolean;
    required?: boolean;
}

export interface FormChipGroupProps {
    error?: boolean;
    errorMessage?: React.ReactNode;
    labelProps?: LabelProps & { label: React.ReactNode };
    inputProps: ChipGroupInputProps;
    fieldGroupProps?: FieldGroupProps;
}

function normalizeSelected(value: SelectedValue | undefined): Array<string | number> {
    if (Array.isArray(value)) {
        return value;
    }
    if (value === undefined || value === null || value === "") {
        return [];
    }
    return [value];
}

function isSelectedValue(selected: Array<string | number>, optionValue: string | number): boolean {
    return selected.some((item) => String(item) === String(optionValue));
}

const FormChipGroup: React.FC<FormChipGroupProps> = ({
    error,
    errorMessage,
    labelProps,
    inputProps,
    fieldGroupProps,
}) => {
    const selected = normalizeSelected(inputProps.value);
    const labelId = inputProps.id ?? `${inputProps.name.replace(/\./g, "-")}-label`;
    const { label, ...restLabelProps } = labelProps ?? { label: undefined };

    const toggle = (optionValue: string | number) => {
        if (inputProps.disabled) {
            return;
        }

        const next = isSelectedValue(selected, optionValue)
            ? selected.filter((item) => String(item) !== String(optionValue))
            : [...selected, optionValue];

        inputProps.onChange?.(next);
    };

    return (
        <FieldGroup error={error} {...fieldGroupProps}>
            {labelProps !== undefined && (
                <Label
                    {...restLabelProps}
                    id={labelId}
                    htmlFor={undefined}
                    disabled={inputProps.disabled}
                    error={error}
                    required={inputProps.required}
                >
                    {label}
                </Label>
            )}
            <div
                className="field-control-chips"
                role="group"
                aria-labelledby={labelProps !== undefined ? labelId : undefined}
            >
                {inputProps.options.map((option) => {
                    const isSelected = isSelectedValue(selected, option.value);
                    const Icon = option.Icon;

                    return (
                        <button
                            key={String(option.value)}
                            type="button"
                            disabled={inputProps.disabled}
                            aria-pressed={isSelected}
                            className={`field-control-chips__item${isSelected ? " is-selected" : ""}`}
                            onClick={() => toggle(option.value)}
                        >
                            {Icon !== undefined && <Icon className="size-4 shrink-0" aria-hidden="true" />}
                            {option.label}
                        </button>
                    );
                })}
            </div>
            {error && errorMessage !== undefined && (
                <Feedback className="field__feedback error">{errorMessage}</Feedback>
            )}
        </FieldGroup>
    );
};

export default FormChipGroup;
